import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, Clock3, Globe, Lightbulb, Search, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { OnboardingStage } from "@runcheetah/shared";

import { Badge } from "@runcheetah/ui";

import {
  ANALYZE_STEPS,
  deriveAudienceCards,
  deriveEditableContentDnaAngles,
  deriveVisualChips,
  deriveVoiceBasics,
  formatAnalyzeExpectationDuration,
  getAnalyzeProgress,
  getInitialAnalyzeStepDisplayStatuses,
  getNextAnalyzeStepDisplayStatuses,
  shouldShowAnalyzeWrappingStep,
  type AnalyzeStepStatus,
  type AnalyzeStepSummary,
  type OnboardingProfileLike,
  type OnboardingVisualLike,
  type OnboardingVoiceLike,
} from "@/lib/brand-onboarding-display";

type BrandStatus = "queued" | "processing" | "scanned" | "failed";

const skeletonBar = (widthClassName: string, delay = 0) => (
  <motion.div
    animate={{ opacity: [0.22, 0.48, 0.22] }}
    transition={{ duration: 1.2, repeat: Infinity, delay }}
    className={`h-3 rounded-full bg-muted ${widthClassName}`}
  />
);

const ANALYZE_TERMINAL_REVEAL_MS = 850;
const ANALYZE_TERMINAL_REVEAL_STAGGER_MS = 260;
const ANALYZE_WRAPPING_DETAIL_INTERVAL_MS = 3_200;

const getFirstStepIndexByStatus = (
  stepStatuses: Record<(typeof ANALYZE_STEPS)[number]["key"], AnalyzeStepStatus>,
  status: AnalyzeStepStatus,
): number => ANALYZE_STEPS.findIndex((step) => stepStatuses[step.key] === status);

const parseCreatedAtMs = (createdAt: string | null | undefined): number | null => {
  if (!createdAt) {
    return null;
  }

  const parsed = new Date(createdAt).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const formatElapsedLabel = (elapsedMs: number, intl: ReturnType<typeof useIntl>): string => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return intl.formatMessage(
      {
        id: "brandOnboarding.analyze.elapsedSecondsOnly",
        defaultMessage: "{seconds}s",
      },
      { seconds },
    );
  }

  return intl.formatMessage(
    {
      id: "brandOnboarding.analyze.elapsedMinutesSeconds",
      defaultMessage: "{minutes}m {seconds}s",
    },
    { minutes, seconds },
  );
};

function AnalyzeWorkspaceMock({
  sourceDomain,
  activeIndex,
  completedIndexes,
  profile,
  voice,
  visual,
}: {
  sourceDomain: string;
  activeIndex: number;
  completedIndexes: number[];
  profile: OnboardingProfileLike | null;
  voice: OnboardingVoiceLike | null;
  visual: OnboardingVisualLike | null;
}) {
  const currentLabel = ANALYZE_STEPS[Math.min(activeIndex, ANALYZE_STEPS.length - 1)]?.label;
  const voiceChips = voice
    ? deriveVoiceBasics(voice)
        .slice(0, 4)
        .map(({ label, value }) => `${label}: ${value}`)
    : [];
  const audienceCards = profile ? deriveAudienceCards(profile).slice(0, 3) : [];
  const persistedContentAngles = voice ? deriveEditableContentDnaAngles(voice).slice(0, 4) : [];
  const contentAngles = persistedContentAngles;
  const visualChips = visual ? deriveVisualChips(visual).slice(0, 3) : [];
  const swatches = visual?.colorPalette?.filter(Boolean).slice(0, 4) ?? [];
  const hasRealColors = swatches.length > 0;
  const hasVisualPreview = hasRealColors || visualChips.length > 0;
  const reveal = (index: number) => completedIndexes.includes(index) || activeIndex >= index;
  const brandMark =
    profile?.brandName?.trim()?.slice(0, 1).toUpperCase() || sourceDomain.slice(0, 1).toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.45 }}
      className="relative overflow-hidden rounded-[1.75rem] border border-border/70 bg-card/95 p-5 shadow-[0_30px_80px_-28px_rgba(15,23,42,0.35)]"
    >
      <motion.div
        animate={{ opacity: [0.42, 0.75, 0.42], scale: [1, 1.04, 1], x: [0, 8, 0], y: [0, -6, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/20 blur-3xl"
      />
      <motion.div
        animate={{
          opacity: [0.28, 0.58, 0.28],
          scale: [1, 1.06, 1],
          x: [0, -10, 0],
          y: [0, 10, 0],
        }}
        transition={{ duration: 8.5, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -bottom-14 -left-12 h-52 w-52 rounded-full bg-amber-100 blur-3xl"
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(168,255,83,0.18),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(212,183,146,0.22),transparent_35%)]" />

      <div className="relative flex items-center gap-2 border-b border-border/60 pb-4">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <div className="ml-2 flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border/70 bg-background/75 px-3 py-1.5 font-mono text-[11px] text-muted-foreground">
          <Globe className="h-3 w-3 shrink-0" />
          <span className="truncate">{sourceDomain}</span>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentLabel}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22 }}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-primary"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <FormattedMessage id="brandOnboarding.analyze.generating" defaultMessage="Generating" />
            <span className="flex items-center gap-1" aria-hidden="true">
              {[0, 1, 2].map((dot) => (
                <motion.span
                  key={dot}
                  animate={{ opacity: [0.2, 1, 0.2], y: [0, -1, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: dot * 0.16 }}
                  className="h-1 w-1 rounded-full bg-current"
                />
              ))}
            </span>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="relative mt-5 space-y-5">
        <div className="space-y-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <FormattedMessage
              id="brandOnboarding.analyze.logoColors"
              defaultMessage="Logo & colors"
            />
          </p>
          <motion.div
            animate={hasVisualPreview ? { opacity: 1 } : { opacity: 0.5 }}
            className="flex items-center gap-4"
          >
            <motion.div
              initial={false}
              animate={
                hasRealColors
                  ? { scale: 1, opacity: 1, filter: "blur(0px)" }
                  : { scale: 0.92, opacity: 0.4, filter: "blur(4px)" }
              }
              transition={{ type: "spring", stiffness: 180, damping: 18 }}
              className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-foreground text-lg font-semibold text-background shadow-lg"
            >
              {brandMark}
            </motion.div>
            <div className="flex flex-wrap gap-2">
              {hasRealColors
                ? swatches.map((color, index) => (
                    <motion.div
                      key={color}
                      initial={false}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ delay: index * 0.06, duration: 0.28 }}
                      className="h-8 w-8 rounded-xl border border-border/70 shadow-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))
                : [0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-8 w-8 rounded-xl border border-border/70 bg-muted/60 shadow-sm blur-sm"
                    />
                  ))}
            </div>
          </motion.div>
          {visualChips.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {visualChips.map((chip, index) => (
                <motion.div
                  key={chip}
                  initial={false}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: index * 0.06, duration: 0.24 }}
                  className="rounded-full border border-border/70 bg-background/75 px-3 py-1.5 text-xs font-medium text-foreground"
                >
                  {chip}
                </motion.div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <FormattedMessage id="brandOnboarding.analyze.voice" defaultMessage="Voice" />
          </p>
          {voiceChips.length ? (
            <div className="flex flex-wrap gap-2">
              {voiceChips.map((tag, index) => (
                <motion.div
                  key={tag}
                  initial={false}
                  animate={
                    reveal(2) ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0.15, scale: 0.9, y: 6 }
                  }
                  transition={{ delay: index * 0.08, duration: 0.28 }}
                  className="rounded-full border border-[#C8FF00]/40 bg-[#C8FF00]/14 px-3 py-1.5 text-xs font-medium text-foreground"
                >
                  {tag}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <div className="rounded-full px-3 py-2">{skeletonBar("w-16")}</div>
              <div className="rounded-full px-3 py-2">{skeletonBar("w-20", 0.1)}</div>
              <div className="rounded-full px-3 py-2">{skeletonBar("w-14", 0.2)}</div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <FormattedMessage id="brandOnboarding.analyze.audience" defaultMessage="Audience" />
          </p>
          {audienceCards.length ? (
            <div className="flex flex-wrap gap-2">
              {audienceCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={false}
                  animate={
                    reveal(3) ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0.15, scale: 0.9, y: 6 }
                  }
                  transition={{ delay: index * 0.08, duration: 0.28 }}
                  className="rounded-full border border-border/70 bg-background/75 px-3 py-1.5 text-xs font-medium text-foreground"
                >
                  {card.title}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="space-y-2 p-3">
              {skeletonBar("w-32")}
              {skeletonBar("w-24", 0.12)}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <FormattedMessage
              id="brandOnboarding.analyze.contentAngles"
              defaultMessage="Content angles"
            />
          </p>
          {contentAngles.length ? (
            <ul className="space-y-2">
              {contentAngles.map((tag, index) => (
                <motion.li
                  key={tag}
                  initial={false}
                  animate={
                    reveal(4) ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0.15, scale: 0.9, y: 6 }
                  }
                  transition={{ delay: index * 0.08, duration: 0.28 }}
                  className="flex items-start gap-2 text-sm text-foreground"
                >
                  <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="leading-5">{tag}</span>
                </motion.li>
              ))}
            </ul>
          ) : (
            <div className="space-y-2 rounded-2xl p-3">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-2">
                  {skeletonBar("w-40")}
                  {skeletonBar("w-28", 0.1)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const analyzeStartKey = (brandId: string) => `brand-analyze-start-${brandId}`;

export function BrandOnboardingAnalyzeStage({
  brandId,
  createdAt,
  sourceDomain,
  stage,
  status,
  profile,
  voice,
  visual,
  analysisSteps,
  estimatedReadyMinutes,
}: {
  brandId: string;
  createdAt: string;
  sourceDomain: string;
  stage: OnboardingStage | null;
  status: BrandStatus;
  profile: OnboardingProfileLike | null;
  voice: OnboardingVoiceLike | null;
  visual: OnboardingVisualLike | null;
  analysisSteps?: AnalyzeStepSummary[] | null;
  estimatedReadyMinutes?: number | null;
}) {
  const intl = useIntl();
  const [now, setNow] = useState(() => Date.now());
  const [startedAtMs, setStartedAtMs] = useState(() => {
    const storageKey = analyzeStartKey(brandId);
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      return Number(cached);
    }
    const fromServer = parseCreatedAtMs(createdAt) ?? Date.now();
    localStorage.setItem(storageKey, String(fromServer));
    return fromServer;
  });
  const { activeIndex: rawActiveIndex, stepStatuses: rawStepStatuses } = getAnalyzeProgress({
    stage,
    status,
    profile,
    voice,
    visual,
    analysisSteps,
  });
  const rawStepStatusKey = ANALYZE_STEPS.map((step) => rawStepStatuses[step.key]).join("|");
  const [displayStepStatuses, setDisplayStepStatuses] = useState(() =>
    getInitialAnalyzeStepDisplayStatuses(rawStepStatuses),
  );
  const [wrappingDetailIndex, setWrappingDetailIndex] = useState(0);
  const stepStatuses = displayStepStatuses;

  const processingIndex = getFirstStepIndexByStatus(stepStatuses, "processing");
  const queuedIndex = getFirstStepIndexByStatus(stepStatuses, "queued");
  const activeIndex =
    processingIndex >= 0 ? processingIndex : queuedIndex >= 0 ? queuedIndex : rawActiveIndex;
  const completedIndexes = ANALYZE_STEPS.flatMap((step, index) =>
    stepStatuses[step.key] === "done" ? [index] : [],
  );
  const allStepsFailed = ANALYZE_STEPS.every((step) => stepStatuses[step.key] === "failed");
  const showWrappingStep = shouldShowAnalyzeWrappingStep({ status, stepStatuses });
  const elapsedMs = Math.max(0, now - startedAtMs);
  const elapsedLabel = formatElapsedLabel(elapsedMs, intl);
  const expectationDuration = formatAnalyzeExpectationDuration(estimatedReadyMinutes);
  const wrappingDetails = [
    intl.formatMessage({
      id: "assistantOnboardingCompletion.progress.finishingUniverse",
      defaultMessage: "Finishing your brand universe...",
    }),
    intl.formatMessage({
      id: "assistantOnboardingCompletion.progress.wrappingContext",
      defaultMessage: "Wrapping the final brand context...",
    }),
    intl.formatMessage({
      id: "assistantOnboardingCompletion.progress.checkingSetup",
      defaultMessage: "Checking the last setup steps...",
    }),
    intl.formatMessage({
      id: "assistantOnboardingCompletion.progress.preparingHandoff",
      defaultMessage: "Preparing the first creative handoff...",
    }),
    intl.formatMessage({
      id: "assistantOnboardingCompletion.progress.almostReady",
      defaultMessage: "Almost ready to move you forward...",
    }),
  ] as const;

  useEffect(() => {
    const timeouts: number[] = [];
    const rawStepStatusValues = rawStepStatusKey.split("|") as AnalyzeStepStatus[];
    const targetStepStatuses = Object.fromEntries(
      ANALYZE_STEPS.map((step, index) => [step.key, rawStepStatusValues[index] ?? "queued"]),
    ) as Record<(typeof ANALYZE_STEPS)[number]["key"], AnalyzeStepStatus>;

    setDisplayStepStatuses((previous) => {
      const next = getNextAnalyzeStepDisplayStatuses({ previous, target: targetStepStatuses });

      next.stagedKeys.forEach((key, index) => {
        const timeoutId = window.setTimeout(
          () => {
            setDisplayStepStatuses((current) => ({
              ...current,
              [key]: targetStepStatuses[key],
            }));
          },
          ANALYZE_TERMINAL_REVEAL_MS + index * ANALYZE_TERMINAL_REVEAL_STAGGER_MS,
        );
        timeouts.push(timeoutId);
      });

      return next.stepStatuses;
    });

    return () => {
      for (const timeout of timeouts) {
        window.clearTimeout(timeout);
      }
    };
  }, [rawStepStatusKey]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!showWrappingStep) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setWrappingDetailIndex((currentIndex) => (currentIndex + 1) % wrappingDetails.length);
    }, ANALYZE_WRAPPING_DETAIL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [showWrappingStep, wrappingDetails.length]);

  useEffect(() => {
    const parsedCreatedAtMs = parseCreatedAtMs(createdAt);
    if (parsedCreatedAtMs !== null) {
      setStartedAtMs(parsedCreatedAtMs);
    }
  }, [createdAt]);

  useEffect(() => {
    if (status === "scanned" || status === "failed") {
      localStorage.removeItem(analyzeStartKey(brandId));
    }
  }, [status, brandId]);

  if (allStepsFailed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.35 }}
        className="mx-auto flex min-h-[70vh] w-full max-w-2xl items-center px-6 py-8"
      >
        <div className="w-full rounded-[1.75rem] border border-destructive/20 bg-card/95 p-6 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.45)] sm:p-8">
          <div className="flex flex-col items-start gap-5 sm:flex-row">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-destructive/20 bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="space-y-2">
              <Badge
                shape="pill"
                variant="outline"
                className="w-fit border-destructive/20 text-destructive"
              >
                <FormattedMessage
                  id="brandOnboarding.analyze.failedBadge"
                  defaultMessage="Failed"
                />
              </Badge>
              <h1 className="text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
                <FormattedMessage
                  id="brandOnboarding.analyze.allFailedTitle"
                  defaultMessage="Brand analysis failed"
                />
              </h1>
              <p className="text-sm leading-6 text-muted-foreground sm:text-base">
                <FormattedMessage
                  id="brandOnboarding.analyze.allFailedDescription"
                  defaultMessage="We couldn't extract enough usable brand signals from this website. Retry onboarding to try again."
                />
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35 }}
      className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8"
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-start">
        <div className="space-y-6">
          <div className="space-y-3">
            <Badge shape="pill" variant="outline" className="w-fit">
              <FormattedMessage id="brandOnboarding.stepOneOfThree" defaultMessage="Step 1 of 3" />
            </Badge>
            <div className="space-y-2">
              <h1 className="text-balance font-display text-4xl font-bold tracking-tight sm:text-5xl">
                <FormattedMessage
                  id="brandOnboarding.analyze.title"
                  defaultMessage="Cheetah is learning your brand."
                />
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                <FormattedMessage
                  id="brandOnboarding.analyze.description"
                  defaultMessage="We are scanning {sourceDomain}, extracting the strongest brand signals, and shaping the first review-ready post set around real source material."
                  values={{ sourceDomain }}
                />
              </p>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[1.75rem] border border-border/70 bg-card/95 p-4 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.45)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(168,255,83,0.14),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_32%)]" />
            <div className="relative space-y-4">
              <div className="flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    <FormattedMessage
                      id="brandOnboarding.analyze.progressTitle"
                      defaultMessage="Analysis progress"
                    />
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                    <Sparkles className="h-3.5 w-3.5" />
                    {expectationDuration ? (
                      <FormattedMessage
                        id="brandOnboarding.analyze.estimatedDuration"
                        defaultMessage="Estimated {duration}"
                        values={{ duration: expectationDuration }}
                      />
                    ) : (
                      <FormattedMessage
                        id="brandOnboarding.analyze.noEstimate"
                        defaultMessage="No estimate yet"
                      />
                    )}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground">
                    <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
                    <FormattedMessage
                      id="brandOnboarding.analyze.elapsed"
                      defaultMessage="Elapsed {duration}"
                      values={{ duration: elapsedLabel }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {ANALYZE_STEPS.map((step, index) => {
                  const Icon = step.icon;
                  const stepStatus = stepStatuses[step.key];
                  const isDone = stepStatus === "done";
                  const isAnalyzing = stepStatus === "processing";
                  const isPartial = stepStatus === "partial";
                  const isFailed = stepStatus === "failed";
                  const statusLabel =
                    stepStatus === "done"
                      ? "Done"
                      : stepStatus === "partial"
                        ? "Found something, digging deeper."
                        : stepStatus === "processing"
                          ? "Analyzing"
                          : stepStatus === "failed"
                            ? "Needs attention"
                            : "Pending";

                  return (
                    <motion.div
                      key={step.key}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.06, duration: 0.24 }}
                      className={[
                        "relative overflow-hidden rounded-2xl border px-4 py-4 transition-colors",
                        isAnalyzing
                          ? "border-transparent bg-card shadow-sm"
                          : isPartial
                            ? "border-amber-500/25 bg-amber-500/10"
                            : isDone
                              ? "border-border/60 bg-card"
                              : isFailed
                                ? "border-destructive/20 bg-destructive/5"
                                : "border-border/60 bg-card/50",
                      ].join(" ")}
                    >
                      {isAnalyzing ? (
                        <motion.div
                          animate={{
                            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                            opacity: [0.45, 0.72, 0.45],
                          }}
                          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                          className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,rgba(168,255,83,0.05),rgba(168,255,83,0.22),rgba(168,255,83,0.06))] bg-[length:220%_100%]"
                        />
                      ) : null}
                      <div className="relative flex items-start gap-3">
                        <motion.div
                          animate={isAnalyzing ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                          transition={{ duration: 1.8, repeat: isAnalyzing ? Infinity : 0 }}
                          className={[
                            "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border",
                            isAnalyzing
                              ? "border-primary/20 bg-primary/10 text-primary"
                              : isPartial
                                ? "border-amber-500/25 bg-amber-500/10 text-amber-700"
                                : isDone
                                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-700"
                                  : isFailed
                                    ? "border-destructive/20 bg-destructive/10 text-destructive"
                                    : "border-border bg-muted/40 text-muted-foreground",
                          ].join(" ")}
                        >
                          {isDone ? (
                            <Check className="h-4 w-4" />
                          ) : isPartial ? (
                            <Search className="h-4 w-4" />
                          ) : (
                            <Icon className="h-4 w-4" />
                          )}
                        </motion.div>
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-foreground">{step.label}</p>
                            <span
                              className={[
                                "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]",
                                isDone
                                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-700"
                                  : isPartial
                                    ? "border-amber-500/25 bg-amber-500/10 text-amber-700"
                                    : isAnalyzing
                                      ? "border-primary/20 bg-primary/10 text-primary"
                                      : isFailed
                                        ? "border-destructive/20 bg-destructive/10 text-destructive"
                                        : "border-border bg-muted/40 text-muted-foreground",
                              ].join(" ")}
                            >
                              {statusLabel}
                            </span>
                            {isAnalyzing ? (
                              <span className="flex items-center gap-1" aria-hidden="true">
                                {[0, 1, 2].map((dot) => (
                                  <motion.span
                                    key={dot}
                                    animate={{ opacity: [0.2, 1, 0.2], y: [0, -1, 0] }}
                                    transition={{
                                      duration: 1.15,
                                      repeat: Infinity,
                                      delay: dot * 0.14,
                                    }}
                                    className="h-1.5 w-1.5 rounded-full bg-primary"
                                  />
                                ))}
                              </span>
                            ) : null}
                          </div>
                          <p className="text-sm leading-6 text-muted-foreground">{step.helper}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                {showWrappingStep ? (
                  <motion.div
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.24 }}
                    className="relative overflow-hidden rounded-[1.35rem] border border-peach/45 bg-[linear-gradient(135deg,rgba(255,211,150,0.26),rgba(168,255,83,0.12),rgba(255,255,255,0.02))] px-4 py-4 shadow-[0_18px_50px_-28px_rgba(15,23,42,0.75)] ring-1 ring-peach/20"
                  >
                    <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-peach/30 blur-2xl" />
                    <motion.div
                      animate={{
                        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                        opacity: [0.2, 0.42, 0.2],
                      }}
                      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                      className="pointer-events-none absolute inset-0 bg-[linear-gradient(110deg,transparent,rgba(255,211,150,0.32),transparent)] bg-[length:220%_100%]"
                    />
                    <div className="relative flex items-start gap-3">
                      <motion.div
                        animate={{ scale: [1, 1.08, 1], rotate: [0, 4, -4, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity }}
                        className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-peach/45 bg-peach/55 text-peach-foreground shadow-sm"
                      >
                        <Sparkles className="h-4 w-4" />
                      </motion.div>
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground sm:text-base">
                            <FormattedMessage
                              id="assistantOnboardingCompletion.progress.label"
                              defaultMessage="Almost done"
                            />
                            <span
                              className="ml-1 inline-flex items-center gap-0.5"
                              aria-hidden="true"
                            >
                              {[0, 1, 2].map((dot) => (
                                <motion.span
                                  key={dot}
                                  animate={{ opacity: [0.2, 1, 0.2], y: [0, -1, 0] }}
                                  transition={{
                                    duration: 1.1,
                                    repeat: Infinity,
                                    delay: dot * 0.16,
                                  }}
                                  className="inline-block h-1 w-1 rounded-full bg-current"
                                />
                              ))}
                            </span>
                          </p>
                          <span className="rounded-full border border-peach/45 bg-peach/45 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-peach-foreground">
                            <FormattedMessage
                              id="brandOnboarding.analyze.wrappingBadge"
                              defaultMessage="Please wait"
                            />
                          </span>
                        </div>
                        <p className="text-sm leading-6 text-foreground/75">
                          {wrappingDetails[wrappingDetailIndex] ?? wrappingDetails[0]}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-24">
          <AnalyzeWorkspaceMock
            sourceDomain={sourceDomain}
            activeIndex={activeIndex}
            completedIndexes={completedIndexes}
            profile={profile}
            voice={voice}
            visual={visual}
          />
        </div>
      </div>
    </motion.div>
  );
}
